// ─────────────────────────────────────────────
// Oike · Mi espacio — keys, labels and limits.
//
// Ale's private space as owner and coach. Only she enters (owner-space.mts
// checks it on the server: Oike administrator AND her email in OIKE_OWNER_EMAIL).
// Nothing here reaches the participants' portal, the teams, the sponsors or
// the other administrators, and nothing from participants comes in.
//
// This stage: Biblioteca de vida and Revisión (goals, reflections and the
// weekly review). Mi día, Mis documentos and Mi marca are documented as the
// next steps (docs/mi-espacio/README.md) and have no screens yet.
// Values stored in Airtable are the English keys; the Spanish labels live here.
// ─────────────────────────────────────────────

export const OWNER_AREAS = [
  { key: 'library', label: 'Biblioteca de vida', available: true },
  { key: 'review', label: 'Mi revisión', available: true },
  { key: 'day', label: 'Mi día', available: true, note: 'Agenda, sesiones, tareas y prioridades. Correo y resumen matinal: próxima etapa.' },
  { key: 'documents', label: 'Mis documentos', available: false, note: 'Contratos, seguros, notas y PDFs consultables, con fuentes y vencimientos.' },
  { key: 'brand', label: 'Mi marca', available: true, note: 'Perfil de marca, plantillas, fotos y piezas exportables (sin publicar ni programar).' },
];

// ── Biblioteca de vida ──

export const LIFE_KINDS = [
  { key: 'book', label: 'Libro' },
  { key: 'article', label: 'Artículo' },
  { key: 'podcast', label: 'Podcast' },
  { key: 'video', label: 'Video' },
  { key: 'course', label: 'Curso' },
  { key: 'note', label: 'Nota propia' },
  { key: 'other', label: 'Otro' },
];
/** Audio and video: locators are minutes (mm:ss), not pages. */
export const TIMED_KINDS = ['podcast', 'video', 'course'];

export const LIFE_STATUSES = [
  { key: 'pending', label: 'Pendiente' },
  { key: 'in_progress', label: 'En curso' },
  { key: 'finished', label: 'Terminado' },
];

/**
 * What Oike knows of a material, computed in code from what is stored (never set by hand):
 * - reference: only its data (title, author, link). Saving it does not mean it was read.
 * - with_notes: my notes or fragments I added.
 * - with_content: content actually extracted (a file read in the browser) or pasted (a transcript, a text).
 */
export const KNOWLEDGE_LEVELS = [
  { key: 'reference', label: 'Referencia guardada', hint: 'Solo conocemos sus datos. Guardarla no significa haberla leído o escuchado, y la IA no tiene su contenido.' },
  { key: 'with_notes', label: 'Con notas o fragmentos', hint: 'Conocemos lo que aportaste: tus notas y los fragmentos que copiaste. No el material completo.' },
  { key: 'with_content', label: 'Con contenido procesado', hint: 'Conocemos el contenido efectivamente extraído o pegado (por ejemplo, las páginas con texto de un PDF).' },
];

/** State of the content of a material (Content Status). */
export const CONTENT_STATUSES = [
  { key: 'none', label: 'Sin contenido' },
  { key: 'extracted', label: 'Texto extraído' },
  { key: 'partial', label: 'Extraído en parte' },
  { key: 'needs_ocr', label: 'Necesita OCR' },
  { key: 'failed', label: 'No se pudo extraer' },
];

export const FRAGMENT_KINDS = [
  { key: 'excerpt', label: 'Fragmento que aporté' },
  { key: 'content', label: 'Contenido extraído o pegado' },
];

export const APPLICATION_STATUSES = [
  { key: 'idea', label: 'Idea' },
  { key: 'trying', label: 'Probándola' },
  { key: 'done', label: 'Hecha' },
  { key: 'dropped', label: 'Descartada' },
];

export const LIFE_TEXT_LIMITS = {
  title: 200,
  creator: 200,
  reference: 500,
  notes: 20000,
  fragment: 4000,
  locator: 80,
  tag: 40,
  idea: 500,
  action: 500,
  result: 2000,
  question: 1000,
};
export const LIFE_TAGS_MAX = 15;
export const LIFE_FRAGMENTS_MAX = 60;        // fragments I add by hand, per material
export const LIFE_CONTENT_CHUNK = 3500;      // characters per stored piece of content
export const LIFE_CONTENT_MAX_CHARS = 400000; // content per material
export const LIFE_CONTENT_BATCH_CHARS = 90000; // content per request (the browser sends it in parts)

/** Formats and what happens with each one (shown in the import panel). */
export const LIFE_FORMATS = [
  { key: 'text', ext: '.txt, .md', label: 'Texto', result: 'Se lee en tu navegador y se guarda como contenido.' },
  { key: 'docx', ext: '.docx', label: 'Word', result: 'Se lee en tu navegador (solo el texto) y se guarda como contenido.' },
  { key: 'pdf', ext: '.pdf', label: 'PDF', result: 'Se lee página por página en tu navegador. Las páginas sin texto (escaneadas) se marcan: necesitan OCR, que todavía no está disponible.' },
  { key: 'subtitles', ext: '.vtt, .srt', label: 'Subtítulos o transcripción', result: 'Se guarda con el minuto de cada parte.' },
  { key: 'media', ext: 'audio y video', label: 'Audio y video', result: 'No se transcriben ni se escuchan. Pega la transcripción o tus notas con el minuto.' },
  { key: 'link', ext: 'enlaces', label: 'Enlaces', result: 'Solo referencia: Oike no los abre ni se los envía a la IA.' },
];
export const LIFE_FILE_MAX_BYTES = 40 * 1024 * 1024;

/** The kind of a file by its name: text | docx | pdf | subtitles | media | unsupported. */
export function lifeFileKind(name) {
  const ext = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] || '';
  if (['txt', 'md', 'markdown'].includes(ext)) return 'text';
  if (ext === 'docx') return 'docx';
  if (ext === 'pdf') return 'pdf';
  if (['vtt', 'srt'].includes(ext)) return 'subtitles';
  if (['mp3', 'm4a', 'wav', 'ogg', 'aac', 'flac', 'mp4', 'mov', 'm4v', 'webm', 'avi', 'mkv'].includes(ext)) return 'media';
  return 'unsupported';
}
export const LIFE_FILE_MESSAGES = {
  media: 'El audio y el video no se transcriben ni se escuchan. Pega la transcripción o tus notas, con el minuto (por ejemplo 12:30).',
  unsupported: 'Este formato no se puede leer. Usa .txt, .md, .docx, .pdf, .vtt o .srt, o pega el texto.',
  too_large: 'El archivo pasa de 40 MB.',
  unreadable: 'No se pudo leer el archivo. No se guardó nada.',
  empty: 'No se encontró texto en el archivo. No se guardó nada.',
  needs_ocr: 'Este PDF no tiene texto seleccionable: parece escaneado. Necesita OCR, que todavía no está disponible. No se guardó contenido; el material sigue como estaba. Puedes copiar fragmentos a mano con su página.',
};

// ── Seconds ↔ mm:ss (audio and video locators) ──

/** "12:30", "1:02:03", "[00:12:30]" or "12m30s" → seconds; null when it is not a time. */
export function parseTime(v) {
  const s = String(v ?? '').trim().replace(/^\[|\]$/g, '');
  let m = s.match(/^(?:(\d{1,2}):)?(\d{1,3}):(\d{2})(?:[.,]\d+)?$/);
  if (m) return (Number(m[1] || 0) * 3600) + Number(m[2]) * 60 + Number(m[3]);
  m = s.match(/^(?:(\d+)h)?\s*(?:(\d+)m(?:in)?)?\s*(?:(\d+)s)?$/i);
  if (m && (m[1] || m[2] || m[3])) return Number(m[1] || 0) * 3600 + Number(m[2] || 0) * 60 + Number(m[3] || 0);
  return null;
}
export function formatTime(sec) {
  if (sec === null || sec === undefined || !Number.isFinite(Number(sec))) return '';
  const n = Math.max(0, Math.round(Number(sec)));
  const h = Math.floor(n / 3600), m = Math.floor((n % 3600) / 60), s = n % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

/** Where a fragment is: page, minute or the free locator. */
export function locatorLabel(x) {
  if (!x) return '';
  if (x.page) return `p. ${x.page}`;
  if (x.timeSeconds !== null && x.timeSeconds !== undefined && x.timeSeconds !== '') return `min ${formatTime(x.timeSeconds)}`;
  return x.locator || '';
}

/**
 * A pasted transcript or a .vtt/.srt file → pieces with their time. Lines that
 * start with a time ("12:30 …", "[00:12:30] …") or cue timings ("00:12:30.000 --> …")
 * open a new piece. Without any time, it is one piece without locator.
 */
export function splitTranscript(text, maxChars = LIFE_CONTENT_CHUNK) {
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const pieces = [];
  let cur = null;
  const push = () => { if (cur && cur.text.trim()) pieces.push({ text: cur.text.trim(), timeSeconds: cur.timeSeconds }); cur = null; };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line === 'WEBVTT' || /^\d+$/.test(line) || /^NOTE\b/.test(line)) continue;
    const cue = line.match(/^(\d{1,2}:\d{2}(?::\d{2})?(?:[.,]\d+)?)\s*-->/);
    const lead = line.match(/^\[?(\d{1,2}:\d{2}(?::\d{2})?)\]?(?:\s+(.*))?$/);
    const t = cue ? parseTime(cue[1]) : lead ? parseTime(lead[1]) : null;
    if (t !== null) {
      // A new piece every ~800 characters: precise minutes without one piece per line
      if (!cur || cur.text.length > Math.min(800, maxChars / 3)) { push(); cur = { text: '', timeSeconds: t }; }
      if (lead && !cue && lead[2]) cur.text += `${lead[2]}\n`;
      continue;
    }
    if (!cur) cur = { text: '', timeSeconds: null };
    if (cur.text.length + line.length > maxChars) { const keep = cur.timeSeconds; push(); cur = { text: '', timeSeconds: keep }; }
    cur.text += `${line}\n`;
  }
  push();
  return pieces;
}

/** Plain text → pieces of at most maxChars, cut at paragraph or sentence ends. */
export function splitText(text, maxChars = LIFE_CONTENT_CHUNK) {
  const out = [];
  let buf = '';
  for (const para of String(text || '').replace(/\r/g, '').split(/\n{2,}/)) {
    const p = para.trim();
    if (!p) continue;
    if ((buf + '\n\n' + p).length <= maxChars) { buf = buf ? `${buf}\n\n${p}` : p; continue; }
    if (buf) out.push(buf);
    buf = '';
    if (p.length <= maxChars) { buf = p; continue; }
    let rest = p;
    while (rest.length > maxChars) {
      const cut = Math.max(rest.lastIndexOf('. ', maxChars), rest.lastIndexOf(' ', maxChars));
      const at = cut > maxChars / 2 ? cut + 1 : maxChars;
      out.push(rest.slice(0, at).trim());
      rest = rest.slice(at).trim();
    }
    buf = rest;
  }
  if (buf) out.push(buf);
  return out;
}

// ── Consultas ──

export const ASK_LIMITS = {
  question: 1000,
  maxPassages: 14,       // fragments sent to the AI per question
  maxChars: 14000,       // characters of material per question
  minScore: 1,           // a passage needs at least one meaningful word of the question
};

// ── Objetivos, reflexiones y revisión semanal ──

export const GOAL_AREAS = [
  { key: 'personal', label: 'Personal' },
  { key: 'professional', label: 'Profesional' },
  { key: 'both', label: 'Ambos' },
];
export const GOAL_STATUSES = [
  { key: 'active', label: 'Activo' },
  { key: 'paused', label: 'En pausa' },
  { key: 'achieved', label: 'Logrado' },
  { key: 'dropped', label: 'Dejado' },
];

/** The daily reflection: short and optional. None of the questions is required. */
export const REFLECTION_QUESTIONS = [
  { key: 'wentWell', field: 'Went Well', label: '¿Qué fue bien?' },
  { key: 'wasHard', field: 'Was Hard', label: '¿Qué me costó?' },
  { key: 'learned', field: 'Learned', label: '¿Qué aprendí?' },
  { key: 'adjust', field: 'Adjust', label: '¿Qué quiero ajustar?' },
  { key: 'tryNext', field: 'Try Next', label: '¿Qué acción quiero probar?' },
];

export const OWNER_TEXT_LIMITS = {
  goalTitle: 200,
  goalWhy: 2000,
  progress: 1000,
  reflectionAnswer: 2000,
  reviewItem: 1000,
};
export const GOAL_PROGRESS_MAX = 100;

/** The weekly review draft. */
export const REVIEW_SECTIONS = [
  { key: 'achievements', label: 'Logros y avances' },
  { key: 'difficulties', label: 'Dificultades o temas que se repiten' },
  { key: 'learnings', label: 'Aprendizajes' },
  { key: 'questions', label: 'Preguntas para profundizar' },
  { key: 'adjustments', label: 'Ajustes para la próxima semana (hasta tres)' },
];
export const REVIEW_MAX_ADJUSTMENTS = 3;
export const REVIEW_MAX_ITEMS = 6;
/** Fewer records than this: the draft says so and leans on questions. */
export const REVIEW_MIN_RECORDS = 3;

/** Questions to help when there is little to work with (no AI needed). */
export const REVIEW_HELP_QUESTIONS = [
  '¿Qué momento de esta semana te gustaría recordar?',
  '¿Qué te quitó más energía y qué te la devolvió?',
  '¿Qué aprendiste, aunque sea pequeño, de algo que leíste, escuchaste o viviste?',
  '¿Avanzaste en alguno de tus objetivos? ¿Qué lo hizo posible?',
  '¿Qué te gustaría hacer distinto la semana que viene?',
];

/** Monday (ISO date) of the week of a date, in local time of the given date string. */
export function weekStartOf(isoDate) {
  const d = new Date(`${String(isoDate).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString().slice(0, 10);
}
export function addDays(isoDate, n) {
  const d = new Date(`${String(isoDate).slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// ── Conversión a herramienta de acompañamiento ──

/** Life kinds → kinds of Method Sources. */
export const METHOD_SOURCE_KIND_OF = { book: 'book', article: 'article', podcast: 'podcast', video: 'video', course: 'course', note: 'own_note', other: 'own_note' };
export const TRANSFER_FRAGMENTS_MAX = 20; // Method Sources keeps up to 20 excerpts

// ── Mi día ──

export const TASK_STATUSES = [
  { key: 'open', label: 'Pendiente' },
  { key: 'done', label: 'Hecha' },
  { key: 'dropped', label: 'Descartada' },
];
export const TASK_PRIORITIES = [
  { key: 'high', label: 'Alta' },
  { key: 'normal', label: 'Normal' },
  { key: 'low', label: 'Baja' },
];
export const DAY_TEXT_LIMITS = { taskTitle: 200, taskNotes: 2000, priorityText: 200 };
/** Up to three priorities of the day, chosen by me (never by the AI). */
export const DAY_PRIORITIES_MAX = 3;
/** A calendar event is the same Oike session when it starts within 5 minutes and ends within 15. */
export const SESSION_MATCH = { startMinutes: 5, endMinutes: 15 };

/** Google Calendar for Mi día: read only, plus the account email to show which one is connected. */
export const CALENDAR_SCOPES = ['openid', 'email', 'https://www.googleapis.com/auth/calendar.events.readonly'];
export const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.events.readonly';
// Mi día · correo: the Gmail connected in Configuración (no connection of its own any more). Only the
// headers (sender, subject, date) of the messages of ONE label Ale picks, read with GET requests only.
export const MAIL_DEFAULT_LABEL = { id: 'STARRED', name: 'Destacados' };
// System labels that can be picked (besides Ale's own labels)
export const MAIL_SYSTEM_LABELS = { STARRED: 'Destacados', IMPORTANT: 'Importantes', INBOX: 'Recibidos' };
export const MAIL_MAX_MESSAGES = 20;
// Only messages from people registered in Oike are shown (contacts and participants, whole address). To find them the
// server walks the label page by page: it stops once it has MAIL_MAX_MESSAGES of them or after MAIL_SCAN_LIMIT messages
// read in one request (whole pages, never cut), and then hands back a cursor to go on with the older ones.
export const MAIL_PAGE_SIZE = 50;
export const MAIL_SCAN_LIMIT = 200;
// Message headers read at a time (Gmail allows about 50 reads per second per user)
export const MAIL_CONCURRENCY = 8;

// ── Mi tiempo ──

/** The categories Oike starts with (editable). Rest, relationships and self-care are never "lost time". */
export const DEFAULT_CATEGORIES = [
  { key: 'work', name: 'Trabajo', maintenance: false },
  { key: 'learning', name: 'Aprendizaje', maintenance: false },
  { key: 'admin', name: 'Administración', maintenance: true },
  { key: 'rest', name: 'Descanso', maintenance: false },
  { key: 'relationships', name: 'Vínculos', maintenance: false },
  { key: 'selfcare', name: 'Cuidado personal', maintenance: false },
  // The areas of my life (keys equal to the old Personal Tasks › Area values, so those tasks keep their area)
  { key: 'personal', name: 'Personal', maintenance: false },
  { key: 'pleasure', name: 'Placer', maintenance: false },
  { key: 'platform', name: 'Plataforma', maintenance: false },
  { key: 'sales', name: 'Ventas', maintenance: false },
];
export const CONTRIBUTIONS = [
  { key: 'direct', label: 'Contribuye directamente' },
  { key: 'indirect', label: 'Contribuye indirectamente' },
  { key: 'maintenance', label: 'Mantenimiento necesario' },
];
export const TIME_LIMITS = { minutes: 24 * 60, note: 500, categoryName: 60, categories: 30, goalsPerTask: 5 };
/** Fewer real records than this in a week: the analysis says it is limited. */
export const TIME_MIN_ENTRIES = 5;

// ── Contador (Mi tiempo) ──
// One timer, kept on the server (a Personal Time Entries record with Entry Status = running). It never stops or
// discounts time by itself: these values only decide when to show a discreet notice and which closed stretch stays
// "por revisar" (pending confirmation, kept apart from confirmed time in every total). Editable by Ale.
export const TIMER_DEFAULTS = { noticeMinutes: 90, reviewMinutes: 240 };
export const TIMER_LIMITS = { noticeMinutes: [15, 480], reviewMinutes: [60, 1440] };
export const ENTRY_STATUSES = [
  { key: 'running', label: 'En curso' },
  { key: 'confirmed', label: 'Registrado' },
  { key: 'review', label: 'Por confirmar' },
];
/** "1:30", "90", "1h 30m", "45m" → minutes; null when it is not a duration. */
export function parseMinutes(v) {
  const s = String(v ?? '').trim().toLowerCase();
  let m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = s.match(/^(?:(\d{1,2})\s*h)?\s*(?:(\d{1,3})\s*m(?:in)?)?$/);
  if (m && (m[1] || m[2])) return Number(m[1] || 0) * 60 + Number(m[2] || 0);
  if (/^\d{1,4}$/.test(s)) return Number(s);
  return null;
}
export const fmtMinutes = (n) => { const v = Math.round(Number(n) || 0); const h = Math.floor(v / 60), m = v % 60; return h ? (m ? `${h} h ${m} min` : `${h} h`) : `${m} min`; };

export const ownerLabel = (list, key) => list.find(x => x.key === key)?.label || key;

// ── Mi día · de un objetivo a tareas (IA) ──
// I write what I want to achieve or do; the AI proposes a short sequence of
// tasks with an area each; I edit, choose and confirm. Nothing is saved before
// that, and the AI never sets dates, deadlines, appointments or messages.
export const PLAN_LIMITS = {
  request: 1000,     // what I want to achieve or do
  note: 1000,        // context I add now (optional)
  answer: 300,       // each answer to a question of the AI
  question: 200,
  detail: 500,
  label: 120,        // the plan's label kept on each task (from my text)
  areaName: 40,
  goalTasks: 15,     // open task titles of the chosen goal (only if I include them)
};
/** The AI proposes at most this many tasks (manageable steps); I can add more by hand up to PLAN_SAVE_MAX. */
export const PLAN_AI_MAX = 7;
export const PLAN_SAVE_MAX = 12;
export const PLAN_QUESTIONS_MAX = 3;
/** A request shorter than this many words gets a question first, without calling the AI. */
export const PLAN_MIN_WORDS = 3;
/** Suggested estimates outside this range (minutes) are dropped. */
export const PLAN_ESTIMATE = { min: 5, max: 480 };
/** Words that mean the text talks about a date or a deadline: shown as a warning, never saved as a date. */
export const PLAN_DATE_WORDS = /\b(\d{1,2}[/-]\d{1,2}([/-]\d{2,4})?|\d{4}-\d{2}-\d{2}|lunes|martes|mi[eé]rcoles|jueves|viernes|s[aá]bado|domingo|ma[ñn]ana|hoy|esta semana|pr[oó]xima semana|fin de mes|fin de a[ñn]o|enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|setiembre|octubre|noviembre|diciembre|fecha l[ií]mite|antes del?|deadline|plazo)\b/i;
